import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  Animated,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';

const { width } = Dimensions.get('window');
const TILE_WIDTH = (width - 60) / 2;

const SUB_TILES = [
  {
    id: 'meridians',
    title: 'Dim mak Meridians',
    icon: '☯',
    navigation: 'DimMakMeridiansBuilder',
  },
  {
    id: 'points',
    title: 'Dim Mak Meridian points',
    icon: '⌖',
    navigation: 'DimMakPointsBuilder',
  },
];

export default function DimMakBuilderHubPage({ navigation, route }) {
  const theme = useTheme();
  const scaleAnim = useRef({}).current;
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const stopwatchRef = useRef(null);

  useEffect(() => {
    const userId = route?.params?.userId || route?.params?.user?.id;
    if (!userId) return;
    fetch(`${getApiUrl()}/api/users/${userId}`)
      .then((res) => res.json())
      .then((data) => setCurrentUser(data))
      .catch((err) => console.error('Error fetching user:', err));
  }, [route]);

  SUB_TILES.forEach((item) => {
    if (!scaleAnim[item.id]) {
      scaleAnim[item.id] = new Animated.Value(1);
    }
  });

  const handleTilePress = (item) => {
    Animated.sequence([
      Animated.timing(scaleAnim[item.id], {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(scaleAnim[item.id], {
        toValue: 1,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();

    navigation.navigate(item.navigation, {
      userId: currentUser?.id || route?.params?.userId || 1,
      user: currentUser,
    });
  };

  const styles = getStyles(theme);

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'dim-mak-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={() =>
            navigation.navigate('BuilderMain', { userId: currentUser?.id || 1, user: currentUser })
          }
          activeOpacity={0.7}
        >
          <Text style={styles.breadcrumb}>‹ Dim mak builder</Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.pageTitle}>Dim mak builder</Text>
        <View style={styles.tilesContainer}>
          {SUB_TILES.map((item, index) => (
            <Animated.View
              key={item.id}
              style={[
                { transform: [{ scale: scaleAnim[item.id] || 1 }] },
                index % 2 === 0 && styles.tileLeft,
                index % 2 === 1 && styles.tileRight,
              ]}
            >
              <TouchableOpacity
                style={styles.tile}
                onPress={() => handleTilePress(item)}
                activeOpacity={0.8}
              >
                <Text style={styles.tileIcon}>{item.icon}</Text>
                <Text style={styles.tileTitle}>{item.title}</Text>
              </TouchableOpacity>
            </Animated.View>
          ))}
        </View>
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    content: { flex: 1 },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    pageTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 24,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    tilesContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    tile: {
      width: TILE_WIDTH,
      height: 180,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: theme.isDarkMode ? 0 : 12,
      padding: 20,
      marginBottom: 20,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: theme.isDarkMode ? 2 : 3,
      borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    },
    tileLeft: { marginRight: 10 },
    tileRight: { marginLeft: 10 },
    tileIcon: {
      fontSize: 48,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    tileTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
  });
